import { useState, useEffect } from "react";
import { Menu } from "antd";
import { NavLink, useLocation } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import { useSupabaseUser, fetchAdminProfile } from "../../hooks/fetch";
import { GiftOutlined, MessageOutlined, AppstoreOutlined, TeamOutlined, AppstoreAddOutlined, DollarOutlined, TagsOutlined, StarOutlined, UsergroupAddOutlined, FileTextOutlined, PictureOutlined } from "@ant-design/icons";
import "../../assets/styles/sidenav.css";
import SidenavPartner from './SidenavPartner';

function Sidenav({ color }) {
  const [userInfo, setUserInfo] = useState(null);
  const { pathname } = useLocation();
  const page = pathname.replace("/", "");
  const [collapsed] = useState(false);

  // Active = toutes les pages du flux Revenus (super_admin)
  const isRevenuActive = page === "Paiement"
    || page === "Sales"
    || page === "Revenu"
    || pathname.toLowerCase().startsWith("/paiement/client");

  const { user } = useSupabaseUser();

  // controls whether the real sidenav is visible (used to animate entrance)
  // default to true so the sidenav is shown immediately and avoids layout delays
  // This component doesn't change visibility at runtime, so keep a constant to avoid an unused setter.
  const visible = true;

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!user || !user.id) {
        if (mounted) setUserInfo(null);
        return;
      }
      const profile = await fetchAdminProfile(user.id);
      if (!mounted) return;
      if (profile) {
        setUserInfo(profile);
      } else {
        setUserInfo(null);
      }
    })();
    return () => { mounted = false; };
  }, [user]);

  // Note: role-based immediate selection is handled by a separate wrapper
  // component (SidenavSelector) to keep hook ordering stable.

  // Icônes (renommées pour éviter conflits)
  // Ant Design icons for all menu items for consistency
  const dashboardIcon = [<AppstoreOutlined style={{ fontSize: 20, color }} key={0} />];
  const salesIcon = [<DollarOutlined style={{ fontSize: 20, color }} key={0} />]; // Revenus
  const partnersIcon = [<TeamOutlined style={{ fontSize: 20, color }} key={0} />];
  const sponsorsIcon = [<GiftOutlined style={{ fontSize: 20, color }} key={0} />];
  const reclamationsIcon = [<MessageOutlined style={{ fontSize: 20, color }} key={0} />];
  const reviewsIcon = [<StarOutlined style={{ fontSize: 20, color }} key={0} />];
  const categoriesIcon = [<AppstoreAddOutlined style={{ fontSize: 20, color }} key={0} />]; // Catégories
  const promosIcon = [<TagsOutlined style={{ fontSize: 20, color }} key={0} />]; // Les promotions
  const publicationsIcon = [<FileTextOutlined style={{ fontSize: 20, color }} key={0} />]; // Publications
  const bannersIcon = [<PictureOutlined style={{ fontSize: 20, color }} key={0} />]; // Banners
  const usersIcon = [<UsergroupAddOutlined style={{ fontSize: 20, color }} key={0} />]; // Utilisateurs

  // We intentionally show the sidenav immediately (visible=true) to avoid layout shift.
  // Profile data (userInfo) still loads in background and will update role-based rendering.

  // First and last name display currently unused (avatar block is commented out).
  // If you re-enable the avatar/user info UI, reintroduce these.
  const userRole = userInfo ? (userInfo.role || "").toLowerCase() : "";

  // While userInfo is loading we still render a lightweight sidebar placeholder
  // to reserve layout space and avoid content shift. Once userInfo is available
  // the real sidebar is rendered.
  // we render the sidenav immediately (no blocking placeholder) to avoid layout shift
  // Render the partner sidenav for sous admin to keep components separated
  if (userRole === "sous admin") {
    return <SidenavPartner color={color} />;
  }

  return (
    <div className={`sidenav-root ${collapsed ? "collapsed" : ""} ${visible ? 'sidenav-visible' : 'sidenav-hidden'}`}>
      <style>{`
        /* entrance animation for the real sidebar */
        .sidenav-root.sidenav-hidden { opacity: 0; transform: translateX(-6px); }
        .sidenav-root.sidenav-visible { opacity: 1; transform: none; transition: opacity .28s ease, transform .28s ease; }
      `}</style>
      {/* Poignée de réduction */}
     
      <div className="brand">
        <div
          style={{
            width: "100%",
            maxWidth: 300,
            position: "relative",
            zIndex: 1,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            className="brand-title"
            style={{
              fontWeight: 700,
              fontSize: 30,
              letterSpacing: 2,
              marginBottom: 10,
              fontFamily: "Bruno Ace SC, sans-serif",
              textTransform: "uppercase",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 0,
              width: "100%",
              marginTop : 10,
            }}
          >
            <span style={{ color: "#222", fontWeight: 700, fontFamily: "Bruno Ace SC, sans-serif" }}>BATI</span>
            <span style={{ color: "#FFF", fontWeight: 700, fontFamily: "Bruno Ace SC, sans-serif" }}>LINK</span>
          </div>
        </div>
      </div>

      <hr />

      <div className="sidenav-menu-container">
        <Menu
          theme="light"
          mode="inline"
          items={[
            { key: '1', label: (<NavLink to="/Dashboard"><span className="icon" style={{ background: page === "Dashboard" ? color : "" }}>{dashboardIcon}</span><span className="label">Tableau de bord</span></NavLink>) },
            { key: '2', label: (<NavLink to="/Paiement"><span className="icon" style={{ background: isRevenuActive ? color : "" }}>{salesIcon}</span><span className="label">Revenus</span></NavLink>) },
            { key: 'users', label: (<NavLink to="/Utilisateurs"><span className="icon" style={{ background: page === "Utilisateurs" ? color : "" }}>{usersIcon}</span><span className="label">Les professionnels</span></NavLink>) },
            { key: '3', label: (<NavLink to="/Partenaires"><span className="icon" style={{ background: page === "Partenaires" ? color : "" }}>{partnersIcon}</span><span className="label">Les partenaires</span></NavLink>) },
            { key: '4', label: (<NavLink to="/Catégories"><span className="icon" style={{ background: page === "Catégories" ? color : "" }}>{categoriesIcon}</span><span className="label">Les Catégories</span></NavLink>) },
            { key: '5', label: (<NavLink to="/Promos"><span className="icon" style={{ background: page === "Promos" ? color : "" }}>{promosIcon}</span><span className="label">Promotions</span></NavLink>) },
            { key: 'publications', label: (<NavLink to="/Publications"><span className="icon" style={{ background: page === "Publications" ? color : "" }}>{publicationsIcon}</span><span className="label">Publications</span></NavLink>) },
            { key: 'banners', label: (<NavLink to="/Banners"><span className="icon" style={{ background: page === "Banners" ? color : "" }}>{bannersIcon}</span><span className="label">Banners</span></NavLink>) },
            { key: '6', label: (<NavLink to="/Sponsors"><span className="icon" style={{ background: page === "Sponsors" ? color : "" }}>{sponsorsIcon}</span><span className="label">Les sponsors</span></NavLink>) },
            { key: 'reviews', label: (<NavLink to="/reviews-list"><span className="icon" style={{ background: page === "reviews-list" ? color : "" }}>{reviewsIcon}</span><span className="label">Les avis</span></NavLink>) },
            { key: '7', label: (<NavLink to="/Réclamations"><span className="icon" style={{ background: page === "Réclamations" ? color : "" }}>{reclamationsIcon}</span><span className="label">Réclamations</span></NavLink>) },
            // { key: 'notifications', label: (<NavLink to="/Notifications"><span className="icon"><BellOutlined style={{ fontSize: 20, color }} /></span><span className="label">Notifications</span></NavLink>) },
          ]}
          onClick={async ({ key }) => {
            if (key === '9') {
              try {
                await supabase.auth.signOut();
                window.location.href = '/sign-in';
              } catch (error) {
                console.error('Erreur lors de la déconnexion:', error?.message || error);
              }
            }
          }}
        />
      </div>
    </div>
  );
}

export default Sidenav;